import MoneyInput from 'components/MoneyInput';
import React from 'react';
import isNil from 'lodash/isNil';
import { Box } from '@mui/material';
import { useResourceContext } from 'ra-core';
import { useFormContext } from 'react-hook-form';
import { DisplayHelpers } from 'utils/helpers';
import { formatCents } from 'utils/rateSettings/SharedRateSettingsForm.utils';
import { RATE_ADJUSTMENT_CENTS } from 'constants/rateSettings';
import { useRateControllerContext } from '../rateController/RateControllerContext';
import {
  deriveInheritedRatePreview,
  toBlendedOverride,
} from '../rateController/rateControllerModel';
import { RateFieldCheckbox } from './RateFieldCheckbox';

const INELIGIBLE_FOR_TEN_CENT_REDUCTION = ['AgencyLocationPositionRateSetting'];

// Zero is never a real Professional Rate — treat it as unset.
const isBlank = (v: unknown): boolean => isNil(v) || v === '' || Number(v) === 0;

type Props = { source: string; inheritedSettings: any };

export const ProfessionalRateInput = ({ source, inheritedSettings }: Props) => {
  const { watch } = useFormContext();
  const resource = useResourceContext();
  const { isControlled } = useRateControllerContext();

  const show_ten_cent_reduction = !INELIGIBLE_FOR_TEN_CENT_REDUCTION.includes(resource);
  const isLocationLevel =
    resource === 'LocationPositionRateSetting' || resource === 'AgencyLocationPositionRateSetting';

  const fieldValue = watch(`${source}.pro_rate_cents`);
  const inheritedPro = deriveInheritedRatePreview({
    inherited: inheritedSettings,
    source,
    isLocationLevel,
    useBlendedOverride: toBlendedOverride(watch(`${source}.use_blended_rate`)),
  }).professionalRate;
  const effectivePro = isBlank(fieldValue) ? inheritedPro : Number(fieldValue);

  const inheritedText = isBlank(fieldValue) ? formatCents(inheritedPro) : null;
  const adjustedText =
    show_ten_cent_reduction && effectivePro !== null && Number.isFinite(effectivePro)
      ? `Adjusted rate: ${DisplayHelpers.money(Math.max(0, effectivePro - RATE_ADJUSTMENT_CENTS))}`
      : null;
  const helperText = [inheritedText, adjustedText].filter(Boolean).join(' — ');

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <MoneyInput
        source={`${source}.pro_rate_cents`}
        label="Professional Rate"
        disabled={!isControlled('professionalRate')}
        sx={{ flex: 1 }}
        helperText={show_ten_cent_reduction ? helperText : null}
      />
      <RateFieldCheckbox code="professionalRate" label="Admin controls Professional Rate" />
    </Box>
  );
};
